import { canonicalExtension } from './filename';
import type {
  ConversionProgress,
  ConversionResult,
  ConvertOptions,
  ConverterCapabilities,
  FileCategory,
  FileMetadata,
} from './types';

export interface ConvertContext {
  signal?: AbortSignal;
  onProgress?: (progress: ConversionProgress) => void;
}

/**
 * 取消检查：抛出 AbortError 让上层统一归一为 CANCELLED。
 * 放在 core 里，避免每个引擎各写一份（长任务必须在关键节点主动检查）。
 */
export function throwIfAborted(context?: ConvertContext): void {
  if (context?.signal?.aborted) {
    const error = new Error('任务已取消');
    error.name = 'AbortError';
    throw error;
  }
}

export interface Converter {
  capabilities(): ConverterCapabilities;
  canConvert(source: FileMetadata, targetExtension: string): boolean;
  inspect(file: File): Promise<FileMetadata>;
  convert(file: File, options?: ConvertOptions, context?: ConvertContext): Promise<ConversionResult>;
  /** 可选：异步探测运行时能力（例如 WASM 编码器可用性），UI 据此禁用不可用目标格式。 */
  probe?(): Promise<ConverterCapabilities>;
}

export class ConverterRegistry {
  private readonly converters = new Map<string, Converter>();

  register(converter: Converter): void {
    const id = converter.capabilities().id;
    if (this.converters.has(id)) throw new Error(`Converter already registered: ${id}`);
    this.converters.set(id, converter);
  }

  byId(id: string): Converter | undefined {
    return this.converters.get(id);
  }

  list(category?: FileCategory): ConverterCapabilities[] {
    const all = [...this.converters.values()].map((converter) => converter.capabilities());
    return category ? all.filter((capability) => capability.category === category) : all;
  }

  /** 全部可接受的源格式（去重后按字母序）。 */
  sourceFormats(): string[] {
    const formats = new Set<string>();
    for (const capability of this.list()) {
      for (const format of capability.sourceFormats) formats.add(canonicalExtension(format));
    }
    return [...formats].sort();
  }

  /** 某个源扩展名可到达的目标格式（唯一来源，UI 不得硬编码）。 */
  targetsFor(extension: string): string[] {
    const target = canonicalExtension(extension);
    const targets = new Set<string>();
    for (const converter of this.converters.values()) {
      const capability = converter.capabilities();
      if (capability.available === false) continue;
      const sources = capability.sourceFormats.map(canonicalExtension);
      if (!sources.includes(target)) continue;
      for (const format of capability.targetFormats) targets.add(canonicalExtension(format));
    }
    return [...targets].sort();
  }

  /**
   * 某个源扩展名 → 目标格式的**全部**候选引擎（保持注册顺序）。
   *
   * 同一个目标格式可能有多条通道（例如 pdf→txt 既有本地文本层提取，也有 AI 识别），
   * UI 据此让用户选择通道，而不是被注册顺序悄悄决定。
   */
  candidatesFor(extension: string, targetExtension: string): ConverterCapabilities[] {
    const source = canonicalExtension(extension);
    const target = canonicalExtension(targetExtension);
    const candidates: ConverterCapabilities[] = [];
    for (const converter of this.converters.values()) {
      const capability = converter.capabilities();
      if (capability.available === false) continue;
      if (!capability.sourceFormats.map(canonicalExtension).includes(source)) continue;
      if (!capability.targetFormats.map(canonicalExtension).includes(target)) continue;
      candidates.push(capability);
    }
    return candidates;
  }

  resolve(source: FileMetadata, targetExtension: string): Converter {
    const target = canonicalExtension(targetExtension);
    for (const converter of this.converters.values()) {
      if (converter.canConvert(source, target)) return converter;
    }
    throw new Error(`No converter for ${source.type.extension} -> ${target}`);
  }
}
